// Desfaz a codificação aplicada pelo automation-api nos uuid que viajam em link de WhatsApp.
// O porquê está em "05 - zandu-automation-api/src/modules/helper/uuid-link-code.ts": o WhatsApp
// passou a ler uuid v4 dentro de link como chave pix aleatória e renderizava só o uuid, não o link.
//
// Tolerante de propósito: mensagem já entregue continua trazendo uuid cru, e o link de login do app
// do cliente vale 21 dias (ver persons.service.ts#567 no events-api). Então o que já tem cara de uuid
// passa intacto, e só o que não tem é que se tenta decodificar. Qualquer coisa inesperada volta como
// veio — quem valida de verdade é o backend, que responde "não encontrado" como sempre respondeu.
//
// Mesma função, por cópia, em "60 - zandu-app-customer/src/lib/uuid-link-code.ts" (repo separado).
// Há DOIS formatos em uso, por consumidor, e isso é proposital — cada página decodifica o que o seu
// gerador emite, e não dá para misturar:
//
//   base64url  links montados pelo automation-api (NPS, confirmação) e o de assinatura de agendamento,
//              todos consumidos por páginas que já esperam esse formato. Backend não alcança o
//              conversions.service do frontend, então precisava de uma implementação própria.
//   base58     links montados aqui no frontend (checkout, assinatura de documento), via
//              window.api.utils.conversions — esquema que a plataforma já usava nos links de
//              formulário muito antes disto (ver uuidToBase58 em modules/utils/conversions.service.ts).
//
// Unificar os dois é tarefa da solução definitiva (link curto com token opaco no tools-api).
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

// contraparte do encodeUuidForLink do automation-api, para links montados aqui no frontend que são
// consumidos por páginas que esperam base64url (hoje: o link de assinatura de agendamento, que cai
// no /login do app do cliente, mesma porta de entrada dos links da automação)
export function encodeUuidForLink(uuid: string): string {
    if (!uuid) return ''
    const hex = String(uuid).replace(/-/g, '')
    if (!/^[0-9a-fA-F]{32}$/.test(hex)) return uuid
    const bytes = new Uint8Array(16)
    for (let i = 0; i < 16; i++) bytes[i] = parseInt(hex.substr(i * 2, 2), 16)
    let bin = ''
    for (let i = 0; i < 16; i++) bin += String.fromCharCode(bytes[i])
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Versão base58, para as páginas públicas cujos links são montados no frontend.
// O round-trip não é zelo excessivo: o bs58 decodifica entrada de qualquer tamanho e o base58ToUuid
// completa com zeros à esquerda, então um valor que nunca foi um uuid viraria um uuid plausível e
// ERRADO em silêncio — e a página chamaria a API com um id inventado. Só aceita o que volta igual.
export function decodeUuidBase58FromLink(value: string): string {
    if (!value) return value
    if (UUID_RE.test(value)) return value
    try {
        const uuid = window.api.utils.conversions.base58ToUuid(value)
        if (!UUID_RE.test(uuid)) return value
        if (window.api.utils.conversions.uuidToBase58(uuid) !== value) return value
        return uuid
    } catch (error) {
        return value
    }
}

export function decodeUuidFromLink(value: string): string {
    if (!value) return value
    if (UUID_RE.test(value)) return value
    try {
        let b64 = String(value).replace(/-/g, '+').replace(/_/g, '/')
        while (b64.length % 4 !== 0) b64 += '='
        const bin = atob(b64)
        if (bin.length !== 16) return value
        let hex = ''
        for (let i = 0; i < 16; i++) hex += bin.charCodeAt(i).toString(16).padStart(2, '0')
        return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-')
    } catch (error) {
        return value
    }
}
